import { useMemo } from 'react';
import { api } from '../../lib/api';
import { displayCents, useMoneyMode } from '../../lib/money';
import { quarterOf } from '../../../shared/finance';
import type { ExpenseWithNames } from '../../../shared/types';
import { BulkActionBar, formatDayMonth, formatMoney, formatMoneyExact, Vital, Vitals } from '../../components/primitives';
import { useBulkSelect } from '../../lib/useBulkSelect';
import { useListKeyboard } from '../../lib/listKeyboard';
import { reportError } from '../../lib/errorEvents';
import { categoryLabel, expenseGroupValue, expenseRowMeta, expenseRowRight, expenseVitalsLine, topExpenseCategory } from '../../lib/financeListMeta';
import { MONTHS_FULL, EXPENSE_WINDOW, matchesQuery, capitalize } from './helpers';

// ── Expenses tab ──────────────────────────────────────────────────────────

export function ExpensesTab({
  expenses,
  query,
  categoryFilter,
  today,
  currentQuarterKey,
  showAll,
  onShowAll,
  onOpen,
  onChanged,
  modalOpen,
  isMobile,
}: {
  expenses: ExpenseWithNames[];
  query: string;
  categoryFilter: string | null;
  today: string;
  currentQuarterKey: string;
  showAll: boolean;
  onShowAll: () => void;
  onOpen: (exp: ExpenseWithNames) => void;
  /** Refetch after a bulk action (Phase 1.13). */
  onChanged: () => void;
  /** True while the new/edit expense modal is open — disables the keyboard layer. */
  modalOpen: boolean;
  /** ≤880px (§8.1 B): two vitals cards, a mono line and month-grouped rows. */
  isMobile: boolean;
}) {
  const mode = useMoneyMode();
  const disp = (exp: ExpenseWithNames) => displayCents(exp.amount_cents, exp.gst_cents, mode);
  const filtered = useMemo(
    () =>
      expenses.filter(
        (exp) =>
          (!categoryFilter || exp.category === categoryFilter) &&
          matchesQuery(query, exp.vendor, exp.description, exp.project_name, exp.category),
      ),
    [expenses, categoryFilter, query],
  );

  // Vitals over all expenses, not the filtered subset.
  const fyStart =
    today >= `${today.slice(0, 4)}-07-01`
      ? `${today.slice(0, 4)}-07-01`
      : `${Number(today.slice(0, 4)) - 1}-07-01`;
  const inQuarter = expenses.filter((e) => quarterOf(e.expense_date)?.key === currentQuarterKey);
  const inFY = expenses.filter((e) => e.expense_date >= fyStart);
  const quarterTotal = inQuarter.reduce((s, e) => s + disp(e), 0);
  const quarterGst = inQuarter.reduce((s, e) => s + e.gst_cents, 0);
  const fyTotal = inFY.reduce((s, e) => s + disp(e), 0);
  const catTotals = new Map<string, number>();
  for (const e of inFY) catTotals.set(e.category, (catTotals.get(e.category) ?? 0) + disp(e));
  const topCat = [...catTotals.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;
  const topCategory = topExpenseCategory(inFY.map((e) => ({ category: e.category, amount: disp(e) })));

  // Month groups, newest first (the API already orders by expense_date DESC).
  const months: { key: string; label: string; rows: ExpenseWithNames[] }[] = [];
  for (const exp of filtered) {
    const key = exp.expense_date.slice(0, 7);
    let m = months[months.length - 1];
    if (!m || m.key !== key) {
      m = {
        key,
        label: `${MONTHS_FULL[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`,
        rows: [],
      };
      months.push(m);
    }
    m.rows.push(exp);
  }
  let remaining = showAll ? Infinity : EXPENSE_WINDOW;
  const visibleMonths: typeof months = [];
  for (const m of months) {
    if (remaining <= 0) break;
    visibleMonths.push({ ...m, rows: m.rows.slice(0, remaining) });
    remaining -= m.rows.length;
  }
  const truncated = !showAll && filtered.length > EXPENSE_WINDOW;

  // Bulk-select + keyboard (Phase 1.13) — same on-screen order as the
  // month groups render below.
  const visibleIds = useMemo(
    () => visibleMonths.flatMap((m) => m.rows.map((e) => e.id)),
    [visibleMonths],
  );
  const bulk = useBulkSelect(visibleIds);
  const kb = useListKeyboard({
    ids: visibleIds,
    enabled: !modalOpen,
    onToggle: bulk.toggle,
    onOpen: (id) => {
      const exp = filtered.find((e) => e.id === id);
      if (exp) onOpen(exp);
    },
    onClear: bulk.clear,
  });
  const runBulkTrash = async () => {
    const ids = bulk.ids;
    if (ids.length === 0) return;
    try {
      await api('/api/admin/finance/expenses/bulk', { method: 'POST', json: { ids, action: 'trash' } });
    } catch (error) {
      reportError(error, 'Could not move the selected expenses to trash.');
      return;
    }
    bulk.clear();
    onChanged();
  };

  // ── ≤880px (§8.1 B) ────────────────────────────────────────────────────
  if (isMobile) {
    const line = expenseVitalsLine({ fyCents: fyTotal, allTimeCount: expenses.length, topCategory });
    return (
      <>
        <Vitals>
          <Vital
            label="This quarter"
            value={formatMoneyExact(quarterTotal)}
            sub={`${currentQuarterKey} · ${inQuarter.length} expenses`}
          />
          <Vital label="GST paid" value={formatMoneyExact(quarterGst)} tone="gold" sub="claimable on BAS" />
        </Vitals>
        <div className="finance-line">
          <span>{line.left}</span>
          <span className="accent">{line.right}</span>
        </div>

        {visibleMonths.map((m, i) => (
          <section className="finance-group-sec" key={m.key}>
            <div className="finance-group">
              <span>{m.label}</span>
              <span className="finance-group-count">{m.rows.length}</span>
              <span className="finance-group-value">{expenseGroupValue(m.rows.map(disp))}</span>
            </div>
            {m.rows.map((exp) => {
              const right = expenseRowRight(exp, disp(exp));
              return (
                <div className="finance-row" key={exp.id}>
                  <button
                    type="button"
                    className="finance-row-open"
                    aria-label={`Edit ${exp.vendor ?? exp.description ?? categoryLabel(exp.category)}`}
                    onClick={() => onOpen(exp)}
                  >
                    <span className="finance-row-col">
                      <span className="finance-row-title">
                        {exp.vendor ?? exp.description ?? categoryLabel(exp.category)}
                      </span>
                      {/* The description leads the meta only when it isn't already the title. */}
                      <span className="finance-row-meta">
                        <span className="who">
                          {expenseRowMeta({ ...exp, description: exp.vendor ? exp.description : null })}
                        </span>
                      </span>
                    </span>
                    <span className="finance-row-right">
                      <span className="finance-row-money">{right.amount}</span>
                      <span className="finance-row-date">{right.date}</span>
                    </span>
                  </button>
                </div>
              );
            })}
            {truncated && i === visibleMonths.length - 1 && (
              <button className="show-more" onClick={onShowAll}>
                Show all {filtered.length} expenses →
              </button>
            )}
          </section>
        ))}
        {filtered.length === 0 && <div className="finance-empty">No expenses match.</div>}
      </>
    );
  }

  return (
    <>
      <Vitals>
        <Vital
          label="This quarter"
          value={formatMoneyExact(quarterTotal)}
          sub={`${currentQuarterKey} · ${inQuarter.length} expenses`}
        />
        <Vital label="GST paid this quarter" value={formatMoneyExact(quarterGst)} tone="gold" sub="claimable on BAS" />
        <Vital label="This FY" value={formatMoneyExact(fyTotal)} sub={`${expenses.length} all time`} />
        <Vital
          label="Top category"
          value={topCat ? capitalize(topCat[0]) : '—'}
          tone="accent"
          sub={topCat ? `${formatMoney(topCat[1])} this FY` : 'no expenses yet'}
        />
      </Vitals>

      {visibleMonths.map((m, i) => (
        <div className="month-group" key={m.key}>
          <div className="group-head">
            <span className="group-title">{m.label}</span>
            <span className="group-count">{m.rows.length}</span>
            <span className="group-value">{formatMoneyExact(m.rows.reduce((s, e) => s + disp(e), 0))}</span>
          </div>
          {m.rows.map((exp) => (
            <div
              className={`row${kb.focusedId === exp.id ? ' kbd-focus' : ''}`}
              key={exp.id}
              onClick={() => onOpen(exp)}
            >
              <span
                className={`bulk-check${bulk.isSelected(exp.id) ? ' on' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  bulk.toggle(exp.id);
                }}
              />
              <span className="row-title" title={exp.vendor ?? exp.description ?? capitalize(exp.category)}>
                {exp.vendor ?? exp.description ?? capitalize(exp.category)}
              </span>
              {exp.vendor && exp.description && (
                <span className="row-context" title={exp.description}>{exp.description}</span>
              )}
              <span className="row-spacer" />
              <span className="cat-pill">{capitalize(exp.category)}</span>
              {exp.project_name && (
                <span className="row-context" title={exp.project_name} style={{ flexShrink: 0 }}>
                  {exp.project_name}
                </span>
              )}
              <span className={`receipt${exp.receipt_url ? ' has' : ''}`}>⎙</span>
              <span className="row-meta dim col-date">{formatDayMonth(exp.expense_date)}</span>
              <span className="row-meta gold col-amt">{formatMoneyExact(disp(exp))}</span>
            </div>
          ))}
          {truncated && i === visibleMonths.length - 1 && (
            <button className="show-more" onClick={onShowAll}>
              Show all {filtered.length} expenses →
            </button>
          )}
        </div>
      ))}
      {filtered.length === 0 && <div className="empty-note">No expenses match.</div>}
      <BulkActionBar
        count={bulk.count}
        actions={[{ key: 'trash', label: 'Trash', danger: true, onClick: runBulkTrash }]}
        onClear={bulk.clear}
      />
    </>
  );
}
